import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { authFetch, authFetchForm } from "../api";
import IconPicker, { iconsToList } from "./IconPicker";

const inputClass = "w-full px-4 py-2 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

// A standalone page (About, Contact, Co-Work, Foundation, Portfolio, ...) is laid out like a Service
// detail page: a compact banner (image, title, description, icons), then a body with rich-text
// content on the left and a square image on the right. Everything here is stored on the page's
// HeroContent record. The Services page has its own dedicated layout, so it gets no body fields.
export default function PageSettings() {
  const { slug } = useParams();
  const [pageTitle, setPageTitle] = useState("");
  const [data, setData] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [bodyImageFile, setBodyImageFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const hasBody = slug !== "services";

  useEffect(() => {
    authFetch("/core/menu/")
      .then((res) => res.json())
      .then((items) => {
        const match = items.find((i) => i.slug === slug);
        setPageTitle(match ? match.title : slug);
      })
      .catch(() => setPageTitle(slug));
  }, [slug]);

  useEffect(() => {
    setData(null);
    setImageFile(null);
    setBodyImageFile(null);
    setMessage("");
    setError("");
    authFetch(`/core/hero/${slug}/`)
      .then((res) => res.json())
      .then(setData)
      .catch(() => setError("Failed to load this page's content."));
  }, [slug]);

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    try {
      const fd = new FormData();
      fd.append("page_title", data.page_title);
      fd.append("tagline", data.tagline);
      fd.append("description", data.description);
      fd.append("icons", data.icons || "");
      if (imageFile) fd.append("image", imageFile);
      if (hasBody) {
        fd.append("body_content", data.body_content || "");
        fd.append("body_button_text", data.body_button_text || "");
        if (bodyImageFile) fd.append("body_image", bodyImageFile);
      }

      const res = await authFetchForm(`/core/hero/${slug}/`, fd);
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setData(updated);
      setImageFile(null);
      setBodyImageFile(null);
      setMessage("Page updated.");
    } catch {
      setError("Failed to save.");
    }
  };

  const bannerPreview = imageFile ? URL.createObjectURL(imageFile) : data?.image;
  const bodyPreview = bodyImageFile ? URL.createObjectURL(bodyImageFile) : data?.body_image;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold mb-1">{pageTitle || slug}</h1>
      <p className="text-sm text-gray-400 mb-6">
        {hasBody
          ? "The browser tab title, the banner at the top of the page, and the body below it - the same layout as a Service detail page."
          : "The browser tab title and the banner at the top of this page."}
      </p>

      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {!data ? (
        <p className="text-gray-400">Loading...</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <h2 className="text-lg font-bold text-white pt-2">Browser Title</h2>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Browser Tab Title</label>
            <input
              className={inputClass}
              value={data.page_title}
              onChange={(e) => setData({ ...data, page_title: e.target.value })}
              placeholder={pageTitle || slug}
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown in the browser tab as "Title - Banner Title". Leave blank to use "{pageTitle || slug}".
            </p>
          </div>

          <h2 className="text-lg font-bold text-white pt-4 border-t border-navy-border">Banner</h2>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Banner Title</label>
            <input
              className={inputClass}
              value={data.tagline}
              onChange={(e) => setData({ ...data, tagline: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Banner Description</label>
            <input
              className={inputClass}
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Icons (shown as "Technologies We Use")</label>
            <IconPicker
              selected={iconsToList(data.icons)}
              onToggle={(key) => {
                const current = iconsToList(data.icons);
                const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
                setData({ ...data, icons: next.join(",") });
              }}
            />
          </div>
          <div>
            <p className="text-sm text-gray-300 mb-2">Banner Image</p>
            {bannerPreview && (
              <img src={bannerPreview} alt="Banner preview" className="w-48 h-32 object-cover rounded-lg border border-navy-border mb-2" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
              className="text-sm text-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to use the default gradient.</p>
          </div>

          {hasBody && (
            <>
              <h2 className="text-lg font-bold text-white pt-4 border-t border-navy-border">Body</h2>
              <p className="text-xs text-gray-500 -mt-2">
                Shown below the banner: the title and description above, your content, and the button on the left,
                with the image on the right. The body is hidden until you add content or an image.
              </p>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Body Content</label>
                <div className="bg-white text-black rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={data.body_content || ""}
                    onChange={(value) => setData({ ...data, body_content: value })}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-300 mb-2">Body Image</p>
                {bodyPreview && (
                  <img src={bodyPreview} alt="Body preview" className="w-40 h-40 object-cover rounded-lg border border-navy-border mb-2" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBodyImageFile(e.target.files[0])}
                  className="text-sm text-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Displayed as a square at the full width of its column - a square image works best.
                </p>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Button Text</label>
                <input
                  className={inputClass}
                  value={data.body_button_text || ""}
                  onChange={(e) => setData({ ...data, body_button_text: e.target.value })}
                  placeholder="Request Quote & Information"
                />
                <p className="text-xs text-gray-500 mt-1">Opens the quote request form. Leave blank to hide the button.</p>
              </div>
            </>
          )}

          {message && <p className="text-sm text-green-400">{message}</p>}
          <button className="px-6 py-2 rounded-lg bg-brand-gradient text-white font-semibold">
            Save Changes
          </button>
        </form>
      )}
    </div>
  );
}
